import type { ReactElement } from "react";
import type { Root } from "react-dom/client";
import { createRoot } from "react-dom/client";

export interface CustomInfoOverlayInterface {
  setMap(map: google.maps.Map | null): void;
  open(position: google.maps.LatLng, reactContent: ReactElement): void;
  close(): void;
  addCloseHandler(handler: () => void): void;
  getIsOpen(): boolean;
}

const designGap = 18;
const windowDivider = 2;

export default function createCustomInfoOverlay(map: google.maps.Map): CustomInfoOverlayInterface {
  const overlay = new google.maps.OverlayView();
  overlay.setMap(map);

  let div: HTMLDivElement | null = null;
  let root: Root | null = null;
  let position: google.maps.LatLng | null = null;
  let content: ReactElement | null = null;
  let isOpen = false;
  let closeHandler: (() => void) | null = null;
  let mapClickListener: google.maps.MapsEventListener | null = null;
  let resizeObserver: ResizeObserver | null = null;
  let resizeListener: (() => void) | null = null;

  function updatePosition() {
    if (!div || !position) return;

    const projection = overlay.getProjection();
    if (!projection) return;

    const point = projection.fromLatLngToDivPixel(position);
    if (!point) return;

    const infoWindowWidth = div.offsetWidth;

    div.style.left = `${point.x - infoWindowWidth / windowDivider}px`;
    div.style.top = `${point.y + designGap}px`;
  }

  overlay.draw = function () {
    updatePosition();
  };

  overlay.onAdd = function () {
    div = document.createElement("div");
    div.style.position = "absolute";
    div.style.display = "none";
    div.className = "custom-info-window";

    const contentDiv = document.createElement("div");
    contentDiv.className = "custom-info-content";
    div.appendChild(contentDiv);

    root = createRoot(contentDiv);

    const panes = this.getPanes();
    if (panes) {
      panes.floatPane.appendChild(div);
    }

    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => {
        if (isOpen) updatePosition();
      });
      resizeObserver.observe(div);
    }

    resizeListener = () => {
      if (isOpen) updatePosition();
    };
    window.addEventListener("resize", resizeListener);

    mapClickListener = google.maps.event.addListener(map, "click", () => {
      close();
    });
  };

  overlay.onRemove = function () {
    if (mapClickListener) {
      google.maps.event.removeListener(mapClickListener);
      mapClickListener = null;
    }

    if (resizeListener) {
      window.removeEventListener("resize", resizeListener);
      resizeListener = null;
    }

    if (resizeObserver) {
      resizeObserver.disconnect();
      resizeObserver = null;
    }

    if (div) {
      if (div.parentNode) {
        div.parentNode.removeChild(div);
      }
      div = null;
    }

    if (root) {
      root.unmount();
      root = null;
    }
  };

  function open(newPosition: google.maps.LatLng, reactContent: ReactElement) {
    if (!div || !root) return;

    position = newPosition;
    content = reactContent;

    root.render(content);

    div.style.display = "block";
    isOpen = true;

    updatePosition();
  }

  function close() {
    if (div) {
      div.style.display = "none";
      isOpen = false;

      if (closeHandler) {
        closeHandler();
      }
    }
  }

  function addCloseHandler(handler: () => void) {
    closeHandler = handler;
  }

  function getIsOpen() {
    return isOpen;
  }

  return {
    setMap: (map) => overlay.setMap(map),
    open,
    close,
    addCloseHandler,
    getIsOpen,
  };
}
